import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { posService, type IPOS } from '@/services';
import { Store, MapPin, Settings } from 'lucide-react';

interface POSSelectionProps {
  onPOSChange?: (pos: IPOS | null) => void;
  className?: string;
  externalPOSList?: IPOS[];
  externalSelectedPOS?: IPOS | null;
  externalIsLoading?: boolean;
}

export const POSSelection: React.FC<POSSelectionProps> = ({ 
  onPOSChange, 
  className, 
  externalPOSList,
  externalSelectedPOS,
  externalIsLoading 
}) => {
  const [posList, setPOSList] = useState<IPOS[]>([]);
  const [selectedPOS, setSelectedPOS] = useState<IPOS | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [showDialog, setShowDialog] = useState(false);

  // Usar datos externos si están disponibles, sino usar el estado local
  const effectivePOSList = externalPOSList || posList;
  const effectiveSelectedPOS = externalSelectedPOS !== undefined ? externalSelectedPOS : selectedPOS;
  const effectiveIsLoading = externalIsLoading !== undefined ? externalIsLoading : isLoading;

  useEffect(() => {
    // Solo cargar POS si no se están usando datos externos
    if (!externalPOSList) {
      loadPOSList();
    }
    if (externalSelectedPOS === undefined) {
      loadSelectedPOSFromStorage();
    }
  }, [externalPOSList, externalSelectedPOS]);

  const loadPOSList = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await posService.getPOSList();
      setPOSList(data);
    } catch (error: any) {
      setError('Error al cargar la lista de POS');
      console.error('Error loading POS list:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSelectedPOSFromStorage = () => {
    try {
      const storedPOS = localStorage.getItem('selected_pos');
      if (storedPOS) {
        const pos = JSON.parse(storedPOS);
        setSelectedPOS(pos);
        onPOSChange?.(pos);
      }
    } catch (error) {
      console.error('Error loading POS from storage:', error);
    }
  };

  const handlePOSSelection = (posId: string) => {
    const pos = effectivePOSList.find(p => p.id.toString() === posId);
    if (pos) {
      // Solo actualizar el estado local si no estamos usando datos externos
      if (externalSelectedPOS === undefined) {
        setSelectedPOS(pos);
      }
      onPOSChange?.(pos);
      
      // Guardar en localStorage solo si no estamos usando datos externos
      if (externalSelectedPOS === undefined) {
        localStorage.setItem('selected_pos', JSON.stringify(pos));
      }
      setShowDialog(false);
    }
  };

  const clearPOSSelection = () => {
    setSelectedPOS(null);
    onPOSChange?.(null);
    localStorage.removeItem('selected_pos');
  };

  if (!effectiveSelectedPOS) {
    return (
      <Card className={`border-amber-200 bg-amber-50 ${className}`}>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Store className="h-5 w-5 text-amber-600" />
              <span className="font-medium text-amber-800">POS no seleccionado</span>
            </div>
            <Button 
              size="sm" 
              onClick={() => setShowDialog(true)}
              disabled={isLoading}
            >
              {isLoading ? 'Cargando...' : 'Seleccionar POS'}
            </Button>
          </div>
          <Alert className="mt-3 border-amber-200 bg-amber-50">
            <AlertDescription className="text-amber-800">
              Debe seleccionar un punto de venta antes de procesar transacciones
            </AlertDescription>
          </Alert>
        </CardContent>
        
        {/* Dialog para seleccionar POS */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Store className="h-5 w-5" />
                Seleccionar Punto de Venta
              </DialogTitle>
              <DialogDescription>
                Elija el POS donde está ubicado para continuar
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              {effectiveIsLoading ? (
                <div className="text-center py-4">
                  <span className="text-muted-foreground">Cargando puntos de venta...</span>
                </div>
              ) : (
                <div>
                  <Select onValueChange={handlePOSSelection}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccione un punto de venta" />
                    </SelectTrigger>
                    <SelectContent>
                      {effectivePOSList.map((pos) => (
                        <SelectItem key={pos.id} value={pos.id.toString()}>
                          <div className="flex items-center justify-between w-full">
                            <div>
                              <div className="font-medium">{pos.nombre}</div>
                              <div className="text-sm text-muted-foreground">{pos.descripcion}</div>
                            </div>
                            <Badge variant={pos.activo ? "default" : "secondary"}>
                              {pos.activo ? "Activo" : "Inactivo"}
                            </Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  {effectivePOSList.length === 0 && !effectiveIsLoading && (
                    <p className="text-center text-muted-foreground py-4">
                      No hay puntos de venta disponibles
                    </p>
                  )}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </Card>
    );
  }

  return (
    <Card className={`border-green-200 bg-green-50 ${className}`}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-green-600" />
            <span className="font-medium text-green-800">POS Seleccionado</span>
            <Badge variant="default" className="bg-green-600">
              Activo
            </Badge>
          </div>
          <Button 
            size="sm" 
            variant="outline"
            onClick={() => setShowDialog(true)}
          >
            <Settings className="h-4 w-4 mr-1" />
            Cambiar
          </Button>
        </div>
        
        <div className="space-y-1">
          <p className="font-semibold text-green-800">{effectiveSelectedPOS.nombre}</p>
          <p className="text-sm text-green-700">{effectiveSelectedPOS.descripcion}</p>
          <p className="text-xs text-green-600">ID: {effectiveSelectedPOS.id}</p>
        </div>
        
        <Button 
          size="sm" 
          variant="ghost" 
          onClick={clearPOSSelection}
          className="mt-2 text-red-600 hover:text-red-700 hover:bg-red-50"
        >
          Deseleccionar POS
        </Button>
        
        {/* Dialog para cambiar POS */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Store className="h-5 w-5" />
                Cambiar Punto de Venta
              </DialogTitle>
              <DialogDescription>
                POS actual: <strong>{effectiveSelectedPOS?.nombre}</strong>
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              {isLoading ? (
                <div className="text-center py-4">
                  <span className="text-muted-foreground">Cargando puntos de venta...</span>
                </div>
              ) : (
                <div>
                  <Select onValueChange={handlePOSSelection} value={effectiveSelectedPOS?.id.toString()}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccione un punto de venta" />
                    </SelectTrigger>
                    <SelectContent>
                      {effectivePOSList.map((pos) => (
                        <SelectItem key={pos.id} value={pos.id.toString()}>
                          <div className="flex items-center justify-between w-full">
                            <div>
                              <div className="font-medium">{pos.nombre}</div>
                              <div className="text-sm text-muted-foreground">{pos.descripcion}</div>
                            </div>
                            <Badge variant={pos.activo ? "default" : "secondary"}>
                              {pos.activo ? "Activo" : "Inactivo"}
                            </Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default POSSelection;
